import { resolvePresetDateRange } from '@dashhudson/dashing-ui/utils/date-ranges';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isFinite from 'lodash/isFinite';
import omit from 'lodash/omit';
// capitalize moved to dashboards-exports.ts
import uniq from 'lodash/uniq';
import { defineStore } from 'pinia';

// originForReport moved to dashboards-exports.ts
import { DashboardAPI } from '@/apis';
import { isCancelError } from '@/apis/abort';
import { getHttpErrorStatus } from '@/apis/http-errors';
import {
  CONTENT_REPORT_MEDIA_LIST_SIZES,
  DASHBOARD_COMPARISON_PRESETS,
  DASHBOARD_OVERVIEW_SORT_OPTIONS,
  DASHBOARD_REPORTING_PRESETS,
  OVERVIEW_SORT_DEFAULT_SELECTION,
} from '@/app/dashboards/constants';
import { DashboardUserEventTracker } from '@/app/dashboards/mixpanel';
import { parseIndustryName } from '@/app/dashboards/utils/benchmarks.utils';
import { V1LayoutAdapter } from '@/app/dashboards/utils/layout';
import { getReportRequestParams, parseQueryToArray } from '@/app/dashboards/utils/report.utils';
import {
  REPORT_CATEGORY_TO_DATA_SOURCE_MAP,
  REPORTS,
  REPORTS_CATEGORIES_WITHOUT_BRAND_SELECTION,
} from '@/app/dashboards/utils/reports.enum';
import { CONTENT_REPORT_TYPES } from '@/app/dashboards/utils/reports.enum.js';
import { extractBrandIdsFromReport } from '@/app/dashboards/utils/tagging.utils';
// filterAndSortUsers moved to dashboards-sharing.ts
import { TEMPLATES } from '@/app/dashboards/utils/templates.enum';
import { BRAND, USER } from '@/models/auth/permissions.enum';
import { INDUSTRY_FOLLOWER_BUCKETS } from '@/models/dashboards/benchmark-type.enum';
import { CHANNELS, compareChannels, isUGCMetric } from '@/models/dashboards/channels.enum';
// AuthAPI moved to dashboards-sharing.ts
import { useAuthStore } from '@/stores/auth';
import { useDashboardPermissionsStore } from '@/stores/dashboard-permissions';
import { useDashboardExportsStore } from '@/stores/dashboards-exports';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useDashboardSharingStore } from '@/stores/dashboards-sharing';
import { useFlagStore } from '@/stores/flag';
// For component-level layout access, use: useDashboardLayout from @/app/dashboards/composables/useDashboardLayout
import { useIdentityStore } from '@/stores/identity';
import { useIntercomStore } from '@/stores/intercom';
import { useMetricsStore } from '@/stores/metrics';
// usePdfStore moved to dashboards-exports.ts
import { useSearchStore } from '@/stores/search';
import { useTrackingStore } from '@/stores/tracking';
// downloadFileFromMemory moved to dashboards-exports.ts
// stringifyQuery moved to dashboards-exports.ts
import {
  browserStorageGetItem,
  browserStorageRemoveItem,
  browserStorageSetItem,
} from '@/utils/browserStorage';
import { logger } from '@/utils/logger';

const mixpanelTracker = new DashboardUserEventTracker();

export const useDashboardsStore = defineStore('dashboards', {
  state: () => ({
    // Dashboards
    _dashboards: [],
    form: {
      name: '',
      brands: [],
      competitors: [],
      contentTagIds: [],
      creators: [],
      messageTagIds: [],
      topicIds: [],
      comparisonTopicIds: [],
      channels: [],
    },
    dashboardsSearchTerm: '',
    dashboardsLowToHigh: false,
    dashboardsSort: OVERVIEW_SORT_DEFAULT_SELECTION.selected.value,
    dashboardsOverviewLoading: false,
    dashboardsError: false,
    currentDashboard: null,
    currentDashboardChannels: [],
    currentDashboardBrandIds: [],
    currentDashboardLoading: false,
    updatingDashboard: false,
    dashboardCustomizeMode: false,
    saveCustomizeMode: false,
    cancelCustomizeMode: false,
    dashboardFormShow: false,
    dashboardFormMode: null,
    dashboardFormTarget: null,
    abortControllers: {},
    // Reports
    reports: {},
    reportLoading: {},
    reportError: {},
    metricTypes: {},
    metricTypesRequestError: false,
    // reportDataCsvLoading moved to dashboards-exports.ts
    previewReportData: null,
    showStaticDatesDropdownReportId: null,
    reportsDataCache: [],
    customMetricsSelected: false,
    // for report preview so that channel icons and custom metric name are displayed
    selectedCustomMetricChannels: [],
    selectedCustomMetricName: [],
    customMetricPreviewTooltip: null,
    // NOTE: Export-related state has been moved to dashboards-exports.ts store
    // NOTE: Sharing-related state has been moved to dashboards-sharing.ts store
    // The getters below delegate to those stores for backwards compatibility
    // Industries
    industries: [],
  }),
  getters: {
    // Dashboards
    dashboards: (state) =>
      [...(state._dashboards || [])].sort((a, b) => a?.name?.localeCompare(b?.name)),
    dashboardsOverview: (state) =>
      state._dashboards.filter((dashboard) =>
        dashboard.name.toLowerCase().includes(state.dashboardsSearchTerm.toLowerCase()),
      ) || [],
    dashboardsOverviewSelectedSort: (state) => ({
      selected:
        DASHBOARD_OVERVIEW_SORT_OPTIONS[state.dashboardsSort] ??
        OVERVIEW_SORT_DEFAULT_SELECTION.selected,
      sortOrder: state.dashboardsLowToHigh ? 'ASC' : 'DESC',
    }),
    dashboardsOverviewFormattedSort: (state) =>
      `${!state.dashboardsLowToHigh ? '-' : ''}${state.dashboardsSort}`,
    ownedDashboards() {
      const authStore = useAuthStore();
      const currentUserId = authStore.identity?.id;
      return this.dashboards.filter((dashboard) => dashboard.user_id === currentUserId);
    },
    sharedDashboards() {
      const authStore = useAuthStore();
      const currentUserId = authStore.identity?.id;
      return this.dashboards.filter((dashboard) => dashboard.user_id !== currentUserId);
    },
    hasDashboards: (state) => state._dashboards?.length > 0,
    currentDashboardId: (state) => state.currentDashboard?.id,
    currentDashboardName: (state) => state.currentDashboard?.name,

    identityBrands: () => {
      const authStore = useAuthStore();
      const identityBrands = authStore.identity?.brands || {};
      return Object.keys(identityBrands).map((key) => identityBrands[key]);
    },
    dashboardIdentityBrands() {
      const authStore = useAuthStore();
      const dashboardPermissionsStore = useDashboardPermissionsStore();

      return this.identityBrands.filter(
        (brand) =>
          authStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand) ||
          dashboardPermissionsStore.canUserAccessDashboards(brand) ||
          false,
      );
    },
    dashboardIdentityBrandsByIds() {
      return this.dashboardIdentityBrands.map((brand) => brand.id);
    },
    dashboardCompetitiveIdentityBrands() {
      const authStore = useAuthStore();
      const dashboardPermissionsStore = useDashboardPermissionsStore();
      return this.dashboardIdentityBrands.filter(
        (brand) =>
          authStore.guard(BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS, brand) ||
          dashboardPermissionsStore.canUserAccessCompetitors(brand) ||
          false,
      );
    },
    dashboardTemplates: () => Object.values(TEMPLATES),
    // Reports
    getMetricTypes: (state) => (channel) => {
      const metricsReport = REPORTS.SINGLE_METRIC.metricTypeReportKey;
      const contentReport = REPORTS.CONTENT_OWNED.metricTypeReportKey;
      const graphReport = REPORTS.GRAPH.metricTypeReportKey;
      if (channel === CHANNELS.INSTAGRAM_UGC.value) {
        return {
          [metricsReport]: [
            ...state.metricTypes[CHANNELS.INSTAGRAM.value][metricsReport],
            ...state.metricTypes[CHANNELS.INSTAGRAM_UGC.value][metricsReport],
          ],
          [contentReport]: state.metricTypes?.[channel]?.[contentReport],
          [graphReport]: [
            ...state.metricTypes[CHANNELS.INSTAGRAM.value][graphReport],
            ...state.metricTypes[CHANNELS.INSTAGRAM_UGC.value][graphReport],
          ],
        };
      }
      return state.metricTypes?.[channel];
    },
    getComparableMetricsForChannels() {
      const dashboardReportsStore = useDashboardReportsStore();
      const metricsStore = useMetricsStore();
      return (targetChannels, reportCategory, reportType) => {
        const isContentReport = CONTENT_REPORT_TYPES.includes(reportType);
        let comparableMetrics = dashboardReportsStore.comparableMetrics;
        if (isContentReport) {
          comparableMetrics = dashboardReportsStore.comparableContentMetrics;
        }
        const allMetrics = Object.keys(comparableMetrics);
        return allMetrics.reduce((acc, metric) => {
          const { data_source: dataSource } =
            metricsStore.getTargetMetricDetailsMultiChannel(metric, isContentReport) ?? {};
          const metricChannels = Object.keys(comparableMetrics[metric]);
          const metricHasTargetChannels = targetChannels.every((channel) =>
            metricChannels.includes(channel),
          );
          const metricIsAllowedForReportCategory =
            REPORT_CATEGORY_TO_DATA_SOURCE_MAP[reportCategory].includes(dataSource);
          if (metricHasTargetChannels && metricIsAllowedForReportCategory) {
            acc.push(metric);
          }
          return acc;
        }, []);
      };
    },
    getComparableMetricKey() {
      const dashboardReportsStore = useDashboardReportsStore();
      return (targetChannels, parentMetric) => {
        let comparable = null;
        Object.keys(dashboardReportsStore.comparableMetrics).forEach((metric) => {
          const isComparable = targetChannels.every(
            (channel) => !!dashboardReportsStore.comparableMetrics[metric]?.[channel],
          );
          const containsParent = Object.values(
            dashboardReportsStore.comparableMetrics[metric],
          ).includes(parentMetric);
          if (isComparable && containsParent) {
            comparable = metric;
          }
        });
        return comparable;
      };
    },
    channels: (state) => Object.keys(state.metricTypes || {}).sort(compareChannels),
    allReportsLoaded: (state) =>
      Object.keys(state.reportLoading).every((id) => !state.reportLoading[id]),
    // Exports - delegated to dashboards-exports store for backwards compatibility
    _currentDashboardAutoExports() {
      return useDashboardExportsStore()._currentDashboardAutoExports;
    },
    allDashboardAutoExports() {
      return useDashboardExportsStore().allDashboardAutoExports;
    },
    autoExportFormShow() {
      return useDashboardExportsStore().autoExportFormShow;
    },
    autoExportFormMode() {
      return useDashboardExportsStore().autoExportFormMode;
    },
    autoExportFormTarget() {
      return useDashboardExportsStore().autoExportFormTarget;
    },
    autoExportListShow() {
      return useDashboardExportsStore().autoExportListShow;
    },
    spreadsheetExportDownloading() {
      return useDashboardExportsStore().spreadsheetExportDownloading;
    },
    reportDataCsvLoading() {
      return useDashboardExportsStore().reportDataCsvLoading;
    },
    currentDashboardAutoExports() {
      return useDashboardExportsStore().currentDashboardAutoExports;
    },
    pdfReady() {
      const searchStore = useSearchStore();
      const authStore = useAuthStore();
      const hasDashboard = !!this.currentDashboard;
      if (hasDashboard) {
        const hasReports = (this.currentDashboard?.layout?.length || 0) > 0;
        if (hasReports) {
          const reportLoadingIds = Object.keys(this.reportLoading);
          const hasReportLoading = reportLoadingIds.length > 0;
          const allReportsLoaded = reportLoadingIds.every((id) => !this.reportLoading[id]);
          const competitorsLoaded = searchStore.competitorsRequestStatus === 'success';
          const hasCompetitiveAccess = authStore.guard([
            BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS,
            USER.COMPETITIVE.CAN_ACCESS_COMPETITORS,
          ]);
          const competitorsReady = !hasCompetitiveAccess || competitorsLoaded;
          return hasReportLoading && allReportsLoaded && competitorsReady;
        }
        // Dashboard with no reports
        return true;
      }
      return false;
    },
    // Sharing - delegated to dashboards-sharing store for backwards compatibility
    _currentDashboardUsers() {
      return useDashboardSharingStore()._currentDashboardUsers;
    },
    shareFormShow() {
      return useDashboardSharingStore().shareFormShow;
    },
    dashboardSharableUsers() {
      return useDashboardSharingStore().dashboardSharableUsers;
    },
    dashboardShareUserSearchTerm() {
      return useDashboardSharingStore().dashboardShareUserSearchTerm;
    },
    dashboardUserUpdating() {
      return useDashboardSharingStore().dashboardUserUpdating;
    },
    isOwner() {
      return useDashboardSharingStore().isOwner;
    },
    isEditor() {
      return useDashboardSharingStore().isEditor;
    },
    isViewer() {
      return useDashboardSharingStore().isViewer;
    },
    currentDashboardUser() {
      return useDashboardSharingStore().currentDashboardUser;
    },
    currentDashboardUsers() {
      return useDashboardSharingStore().currentDashboardUsers;
    },
    currentDashboardRole() {
      return useDashboardSharingStore().currentDashboardRole;
    },
    // Industries //
    industryBenchmarkIdentityBrands() {
      return this.identityBrands.filter(
        (brand) => brand?.permissions?.dashboard?.can_access_industry_benchmarks || false,
      );
    },
    getBenchmarkOptions: (state) =>
      state.industries.map((industry) => {
        const followerBucket = industry?.follower_bucket || '';
        const industryName = industry?.name || '';
        const cleanedFollowerBucketName = INDUSTRY_FOLLOWER_BUCKETS[followerBucket].text;
        const text = parseIndustryName(
          industryName,
          cleanedFollowerBucketName,
          industry.dashboard_follower_buckets,
        );
        const value = parseIndustryName(industry.id, followerBucket, true);

        return {
          value,
          text,
          id: industry.id,
          follower_bucket: followerBucket,
          hasFollowerBucket: industry.dashboard_follower_buckets,
          label: text,
        };
      }),
    reportsWithStaticDates() {
      return Object.values(this.reports).filter((report) => {
        const hasStaticDates =
          report?.meta?.static_dates?.start_date && report?.meta?.static_dates?.end_date;
        const brandIds = extractBrandIdsFromReport(report);
        const accessibleBrandIds = this.dashboardIdentityBrandsByIds;

        if (REPORTS_CATEGORIES_WITHOUT_BRAND_SELECTION.includes(report?.product_category)) {
          return hasStaticDates;
        }
        return (
          hasStaticDates && brandIds?.every((brandId) => accessibleBrandIds?.includes(brandId))
        );
      });
    },
    hasAnyStaticDates: (state) => state.reportsWithStaticDates?.length > 0,
    allReportSourceAdAccountIds(state) {
      return uniq(
        Object.values(state.reports)
          .filter((report) => report?.meta?.source_ad_account_ids?.length > 0)
          .flatMap((report) => report.meta.source_ad_account_ids),
      );
    },
    mixpanelTracker() {
      return mixpanelTracker;
    },
  },
  actions: {
    // Dashboards

    resetDashboardForm() {
      this.form = {
        name: '',
        brands: [],
        competitors: [],
        creatorIds: [],
        contentTagIds: [],
        messageTagIds: [],
        topicIds: [],
        comparisonTopicIds: [],
        channels: [],
      };
    },
    updateCurrentDashboard({ dashboard, users, identity, autoExports }) {
      this.currentDashboard = dashboard;
      this.currentDashboardChannels = dashboard?.channels || [];
      this.currentDashboardBrandIds = dashboard?.brand_ids || [];
      useDashboardSharingStore().updateCurrentDashboardUsers(users);
      useDashboardExportsStore().updateCurrentDashboardAutoExports(autoExports);
      // Remove reports from previous dashboard
      const reportIdsInNewDashboard = new Set(
        (dashboard?.layout || []).map((item) => item.report_id),
      );
      Object.keys(this.reports).forEach((reportId) => {
        if (!reportIdsInNewDashboard.has(Number(reportId))) {
          delete this.reports[reportId];
        }
      });
      browserStorageSetItem(`currentDashboardId:${identity?.id}`, dashboard?.id);
    },
    clearCurrentDashboard({ identity }) {
      this.currentDashboard = null;
      useDashboardSharingStore().clearCurrentDashboardUsers();
      useDashboardExportsStore().clearCurrentDashboardAutoExports();
      browserStorageRemoveItem(`currentDashboardId:${identity?.id}`);
    },
    addDashboardToList(incomingDashboard) {
      const found = this._dashboards.some((dashboard) => dashboard.id === incomingDashboard.id);
      if (found) {
        this._dashboards = this._dashboards.map((dashboard) =>
          dashboard.id === incomingDashboard.id ? incomingDashboard : dashboard,
        );
      } else {
        this._dashboards = [...this._dashboards, incomingDashboard];
      }
    },
    removeDashboardsFromList({ dashboardIds, identity }) {
      const dashboardIdsToRemove = dashboardIds || [];
      if (dashboardIdsToRemove.includes(this.currentDashboard?.id)) {
        browserStorageRemoveItem(`currentDashboardId:${identity?.id}`);
      }
      this._dashboards = this._dashboards.filter(
        (dashboard) => !dashboardIdsToRemove.includes(dashboard.id),
      );
    },
    launchDashboardCustomizeMode() {
      this.dashboardCustomizeMode = true;
    },
    closeCustomizeMode() {
      this.dashboardCustomizeMode = false;
    },
    launchCreateDashboardForm() {
      this.mixpanelTracker.createDashboardClick();
      this.dashboardFormMode = 'CREATE';
      this.dashboardFormShow = true;
    },
    launchDuplicateDashboardForm(dashboardToDuplicate) {
      this.dashboardFormMode = 'DUPLICATE';
      this.dashboardFormTarget = dashboardToDuplicate;
      this.dashboardFormShow = true;
    },

    launchRenameDashboardForm(dashboardToRename) {
      this.dashboardFormMode = 'RENAME';
      this.dashboardFormTarget = dashboardToRename;
      this.dashboardFormShow = true;
    },
    closeDashboardForm() {
      this.dashboardFormMode = null;
      this.dashboardFormTarget = null;
      this.dashboardFormShow = false;
    },
    requestDashboardCustomizeCancel() {
      this.cancelCustomizeMode = !this.cancelCustomizeMode;
    },
    requestDashboardCustomizeSave() {
      this.saveCustomizeMode = !this.saveCustomizeMode;
    },
    async getDashboards(sort = 'NAME') {
      this.dashboardsOverviewLoading = true;
      this.dashboardsError = false;
      try {
        const response = await DashboardAPI.getDashboards({ limit: 1000, sort });
        const data = response.data.data;
        this._dashboards = data;
      } catch (error) {
        this.dashboardsError = true;
        throw error;
      } finally {
        this.dashboardsOverviewLoading = false;
      }
    },
    async getDashboardsOverview(query = {}) {
      this.dashboardsOverviewLoading = true;
      const { sortParam, searchTerm } = this.extractSortAndSearchParams(
        query,
        browserStorageGetItem('dashboardOverviewSort'),
        browserStorageGetItem('dashboardOverviewSearch'),
      );
      this.dashboardsSearchTerm = searchTerm;
      await this.getDashboards(sortParam);
      this.dashboardsOverviewLoading = false;
    },
    extractSortAndSearchParams(query, storedSort, storedSearch) {
      const { sort: localSort = undefined, search: localSearch = '' } = query;
      if (storedSort || storedSearch || localSort) {
        this.processNewSortParameter(storedSort ?? localSort);
      }
      return {
        sortParam: storedSort ?? localSort,
        searchTerm: storedSearch ?? localSearch,
      };
    },
    processNewSortParameter(sortValue) {
      if (!sortValue) return;
      this.dashboardsLowToHigh = !sortValue.startsWith('-');
      this.dashboardsSort = sortValue.replace('-', '');
    },
    updateDashboardsOverviewSort({ sort }) {
      this.dashboardsSort = sort.selected.value;
      this.dashboardsLowToHigh = sort.sortOrder === 'ASC';
      browserStorageSetItem('dashboardOverviewSort', this.dashboardsOverviewFormattedSort);
    },
    updateDashboardsOverviewSearch({ search }) {
      if (!search) {
        browserStorageRemoveItem('dashboardOverviewSearch');
      } else {
        browserStorageSetItem('dashboardOverviewSearch', search);
      }
      this.dashboardsSearchTerm = search ?? '';
    },
    loadLocalDashboardState({
      dashboardId,
      reportDateRange,
      contextDateRange,
      graphScale,
      reportingPeriodMode,
      contextPeriodMode,
      contentTagIds,
      contentTagMatchType,
      messageTagIds,
      messageTagMatchType,
      brandIds,
      brandTagIds,
      competitorSourceAccountIds,
      competitorTagIds,
      campaignIds,
      boardIds,
    }) {
      const authStore = useAuthStore();
      const dashboardReportsStore = useDashboardReportsStore();
      const identity = authStore.identity;

      dashboardReportsStore.updateReportingPeriodMode({
        mode: reportingPeriodMode,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateContextPeriodMode({
        mode: contextPeriodMode,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      const { reportDateRange: newReportDateRange, contextDateRange: newContextDateRange } =
        this.getDateRanges({
          reportingPeriodMode,
          contextPeriodMode,
          reportDateRange,
          contextDateRange,
        });

      dashboardReportsStore.updateReportDate({
        newReportDateRange,
        dashboardId,
        currentDashboard: this.currentDashboard,
        timeZone: identity?.time_zone_name,
      });

      dashboardReportsStore.updateContextDate({
        newContextDateRange,
        dashboardId,
        currentDashboard: this.currentDashboard,
        timeZone: identity?.time_zone_name,
      });

      dashboardReportsStore.updateGraphScale({
        scale: graphScale,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateContentTagIds({
        contentTagIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateContentTagMatchType({
        contentTagMatchType,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateMessageTagIds({
        messageTagIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateMessageTagMatchType({
        messageTagMatchType,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateBrandIds({
        brandIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateBrandTagIds({
        brandTagIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateCompetitorSourceAccountIds({
        competitorSourceAccountIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateCompetitorTagIds({
        competitorTagIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateCampaignIds({
        campaignIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });

      dashboardReportsStore.updateBoardIds({
        boardIds,
        dashboardId,
        currentDashboard: this.currentDashboard,
      });
    },
    getDateRanges({ reportingPeriodMode, contextPeriodMode, reportDateRange, contextDateRange }) {
      let newReportDateRange = reportDateRange;
      let newReportingPeriod;
      if (reportingPeriodMode) {
        newReportingPeriod = this.getReportDateRange(reportingPeriodMode, reportDateRange);
        newReportDateRange = [
          dayjs(newReportingPeriod.range.start).format('YYYY-MM-DD'),
          dayjs(newReportingPeriod.range.end).format('YYYY-MM-DD'),
        ];
      }
      let newContextDateRange = contextDateRange;
      if (contextPeriodMode && newReportingPeriod) {
        const newContextPeriod = this.getContextDateRange(
          contextPeriodMode,
          newReportingPeriod,
          contextDateRange,
        );
        newContextDateRange = [
          dayjs(newContextPeriod.range.start).format('YYYY-MM-DD'),
          dayjs(newContextPeriod.range.end).format('YYYY-MM-DD'),
        ];
      }
      return {
        reportDateRange: newReportDateRange,
        contextDateRange: newContextDateRange,
      };
    },
    getReportDateRange(reportingPeriodMode, reportDateRange) {
      let range = undefined;
      if (reportingPeriodMode === 'CUSTOM' && reportDateRange) {
        range = {
          start: reportDateRange[0],
          end: reportDateRange[1],
        };
      }
      return resolvePresetDateRange(reportingPeriodMode, {
        presets: DASHBOARD_REPORTING_PRESETS,
        includeTodayInPreset: false,
        range,
      });
    },
    getContextDateRange(contextPeriodMode, reportingPeriod, contextDateRange) {
      let range = undefined;
      if (contextPeriodMode === 'CUSTOM' && contextDateRange) {
        range = {
          start: contextDateRange[0],
          end: contextDateRange[1],
        };
      }
      return resolvePresetDateRange(contextPeriodMode, {
        presets: DASHBOARD_COMPARISON_PRESETS,
        targetRange: reportingPeriod.range,
        range,
      });
    },
    async initCurrentDashboard({
      id,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      graphScale,
      reportingPeriodMode,
      contextPeriodMode,
      contentTagIds,
      contentTagMatchType,
      messageTagIds,
      messageTagMatchType,
      brandIds,
      brandTagIds,
      competitorSourceAccountIds,
      competitorTagIds,
      campaignIds,
      boardIds,
      force = true,
    }) {
      if (id) {
        this.loadLocalDashboardState({
          dashboardId: id,
          reportDateRange: [startDate, endDate],
          contextDateRange: [contextStartDate, contextEndDate],
          graphScale,
          reportingPeriodMode,
          contextPeriodMode,
          contentTagIds: parseQueryToArray(contentTagIds),
          contentTagMatchType,
          messageTagIds: parseQueryToArray(messageTagIds),
          messageTagMatchType,
          brandIds: parseQueryToArray(brandIds),
          brandTagIds: parseQueryToArray(brandTagIds),
          competitorSourceAccountIds: parseQueryToArray(competitorSourceAccountIds),
          competitorTagIds: parseQueryToArray(competitorTagIds),
          campaignIds: parseQueryToArray(campaignIds),
          boardIds: parseQueryToArray(boardIds),
        });
      }
      return this.setCurrentDashboard({
        id,
        force,
      });
    },
    async setCurrentDashboard({ id, force = true } = {}) {
      const identityStore = useIdentityStore();
      const identity = identityStore.identity;

      let dashboard;
      let users;
      let autoExports;
      const dashboardIdsToRemove = [];
      try {
        this.currentDashboardLoading = true;

        let dashboardIdsToTry = [id];
        if (force) {
          // add last dashboardId from local storage
          const localStorageDashboardId = browserStorageGetItem(
            `currentDashboardId:${identity?.id}`,
          );
          dashboardIdsToTry.push([localStorageDashboardId]);

          // Add all other dashboardIds in order of the list api call
          const dashboards = this._dashboards || [];
          const dashboardIds = dashboards.map((dashboardObj) => dashboardObj.id);
          dashboardIdsToTry = dashboardIdsToTry.concat(dashboardIds);
        }

        // convert any strings in the list to numbers
        dashboardIdsToTry = dashboardIdsToTry
          .map((dashboardId) => parseInt(dashboardId, 10))
          .filter((dashboardId) => isFinite(dashboardId));

        for (let i = 0; i < dashboardIdsToTry.length; i += 1) {
          const dashboardId = dashboardIdsToTry[i];
          if (dashboardId) {
            try {
              const [dashboardResponse, usersResponse, autoExportsResponse] = await Promise.all([
                DashboardAPI.getDashboard({ dashboardId }),
                !identityStore.isShared
                  ? DashboardAPI.getDashboardUsers({ dashboardId })
                  : undefined,
                !identityStore.isShared
                  ? DashboardAPI.getDashboardAutomatedExports({ dashboardId })
                  : undefined,
              ]);
              dashboard = dashboardResponse?.data;
              users = usersResponse?.data;
              autoExports = autoExportsResponse?.data;
              break;
            } catch (e) {
              if (e.response?.status >= 400 && e.response?.status < 499) {
                logger.warn(`Failed to load dashboard ${dashboardId}`);
                dashboardIdsToRemove.push(dashboardId);
              } else {
                throw e;
              }
            }
          }
        }
      } finally {
        // remove any dashboards which are no longer accessible since the last refresh
        this.removeDashboardsFromList({ dashboardIds: dashboardIdsToRemove, identity });
        if (dashboard) {
          this.updateCurrentDashboard({ dashboard, users, identity, autoExports });
        } else {
          this.clearCurrentDashboard({ identity });
        }
        this.loadLocalDashboardState({
          dashboardId: dashboard?.id,
        });
        this.currentDashboardLoading = false;
      }
      return dashboard;
    },
    async refreshDashboard() {
      return this.setCurrentDashboard({ id: this.currentDashboardId });
    },
    async internalUpdateDashboard(updateDashboard) {
      const authStore = useAuthStore();
      const identity = authStore.identity;
      this.updatingDashboard = true;
      try {
        const response = await DashboardAPI.updateDashboard({
          dashboardId: updateDashboard.id,
          data: omit(updateDashboard, ['id', 'label']),
        });
        const updatedDashboard = response?.data;
        if (this.currentDashboard?.id === updatedDashboard.id) {
          this.updateCurrentDashboard({
            dashboard: updatedDashboard,
            users: this._currentDashboardUsers,
            autoExports: this._currentDashboardAutoExports,
            identity,
          });
        }
        this.addDashboardToList(updatedDashboard);
        return response;
      } catch (error) {
        if (getHttpErrorStatus(error) !== 404) {
          throw error;
        }
      } finally {
        this.updatingDashboard = false;
      }
      return null;
    },
    async updateDashboard(updateDashboard) {
      const previousDashboard =
        this.dashboardFormTarget?.id === updateDashboard?.id ? this.dashboardFormTarget : undefined;
      await this.internalUpdateDashboard(updateDashboard);
      useTrackingStore().track('Dashboard Edit Dashboard', {
        dashboardId: updateDashboard.id,
        dashboardName: updateDashboard.name,
        previoudDashboardName: previousDashboard?.name,
      });
    },
    async duplicateDashboard({ duplicatePayload }) {
      const data = {
        name: duplicatePayload?.name ?? this.form.name,
        meta: duplicatePayload?.meta ?? {},
      };

      if (!duplicatePayload.meta) delete data.meta;

      const response = await DashboardAPI.createDashboard({
        data,
        params: {
          source_id: this.dashboardFormTarget?.id,
        },
      });
      const createdDashboard = response?.data;
      const eventName = 'Dashboard Create Dashboard';
      const eventProperties = {
        dashboardId: createdDashboard?.id,
        dashboardName: createdDashboard?.name,
        sourceDashboardId: this.dashboardFormTarget?.id,
        duplicate: true,
      };
      useTrackingStore().track(eventName, eventProperties);
      useIntercomStore().trackEvent(eventName, eventProperties);
      // Add to list first so the side menu has the new dashboard before navigation
      this.addDashboardToList(createdDashboard);
      await this.setCurrentDashboard({ id: createdDashboard.id });
      return createdDashboard;
    },
    async deleteDashboard(dashboardToDelete) {
      const authStore = useAuthStore();
      const identity = authStore.identity;
      await DashboardAPI.deleteDashboard({
        dashboardId: dashboardToDelete.id,
      });
      useTrackingStore().track('Dashboard Delete Dashboard', {
        dashboardId: this.dashboardFormTarget?.id,
        dashboardName: this.dashboardFormTarget?.name,
      });
      this.removeDashboardsFromList({ dashboardIds: [dashboardToDelete.id], identity });
      if (this.currentDashboardId === dashboardToDelete.id) {
        this.clearCurrentDashboard({ identity });
      }
      await this.refreshDashboard();
    },
    async createDashboard({ newDashboard, mixpanelPayload = {} } = {}) {
      const payload = {
        name: newDashboard.name,
      };
      if (newDashboard?.layout) {
        payload.layout = newDashboard.layout;
      }
      const response = await DashboardAPI.createDashboard({
        data: payload,
      });

      const createdDashboard = response?.data;

      if (createdDashboard) {
        createdDashboard.report_count = createdDashboard.report_count || 0;
        createdDashboard.brand_count = createdDashboard.brand_count || 0;
        createdDashboard.competitor_count = createdDashboard.competitor_count || 0;
      }
      const mixpanelProperties = {
        dashboardId: createdDashboard?.id,
        dashboardName: createdDashboard?.name,
        duplicate: false,
      };

      if (newDashboard?.templateName) {
        mixpanelProperties.templateType = newDashboard.templateName;
      }

      mixpanelTracker.dashboardCreateDashboard({
        payload: mixpanelProperties,
        newDashboard,
        mixpanelPayload,
      });

      // Add to list first so the side menu has the new dashboard before navigation
      try {
        const refreshedResponse = await DashboardAPI.getDashboard({
          dashboardId: createdDashboard.id,
        });
        const refreshedDashboard = refreshedResponse?.data;
        this.addDashboardToList(refreshedDashboard);
        await this.setCurrentDashboard({ id: refreshedResponse.id });
        return refreshedDashboard;
      } catch {
        this.addDashboardToList(createdDashboard);
        await this.setCurrentDashboard({ id: createdDashboard.id });
      }
      return createdDashboard;
    },

    // Reports
    addReport({ report }) {
      if (report?.id) {
        this.reports = {
          ...this.reports,
          [report.id]: report,
        };
      }
    },
    setReportLoading({ reportId, loading }) {
      this.reportLoading = {
        ...this.reportLoading,
        [reportId]: loading,
      };
    },
    setReportError({ reportId, error }) {
      this.reportError = {
        ...this.reportError,
        [reportId]: error,
      };
    },
    clearReports() {
      this.reports = {};
    },
    removeReport(reportToRemove) {
      if (reportToRemove?.id) {
        const reports = { ...this.reports };
        delete reports[reportToRemove.id];

        this.reports = reports;
      }
    },
    scrollToRecentlyAddedReport(reportId) {
      const element = document.querySelector(`[data-test="ReportPanel-${reportId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async fetchMetricTypes() {
      try {
        const response = await DashboardAPI.getMetricTypes();
        this.metricTypes = response?.data;
      } catch (error) {
        if (error?.response?.status >= 400) {
          this.metricTypesRequestError = true;
        }
      }
    },
    async getReportData({ reportId, refresh }) {
      useFlagStore();
      const {
        reportDateRangeInUserTimezone,
        contextDateRangeInUserTimezone,
        graphScale,
        contentTagIds,
        contentTagMatchType,
        messageTagIds,
        messageTagMatchType,
        brandIds,
        brandTagIds,
        competitorSourceAccountIds,
        competitorTagIds,
        campaignIds,
        boardIds,
      } = useDashboardReportsStore();
      const cachedReport = this.reports?.[reportId];
      if (cachedReport && !refresh) {
        return;
      }

      const abortKey = `fetchReport${reportId}`;
      this.abortControllers[abortKey]?.abort();
      this.abortControllers[abortKey] = new AbortController();
      const { signal } = this.abortControllers[abortKey];
      this.setReportLoading({ reportId, loading: true });
      try {
        const params = {
          ...getReportRequestParams(
            reportDateRangeInUserTimezone,
            contextDateRangeInUserTimezone,
            graphScale,
            contentTagIds,
            contentTagMatchType,
            messageTagIds,
            messageTagMatchType,
            brandIds,
            brandTagIds,
            competitorSourceAccountIds,
            competitorTagIds,
            campaignIds,
            boardIds,
          ),
          limit: CONTENT_REPORT_MEDIA_LIST_SIZES.DASHBOARDS,
        };
        const response = await DashboardAPI.getReport({
          dashboardId: this.currentDashboardId,
          reportId,
          signal,
          params,
        });
        const report = response?.data;
        this.addReport({ report });
        this.setReportLoading({ reportId, loading: false });
      } catch (error) {
        if (!isCancelError(error)) {
          this.setReportError({ reportId, error });
          this.setReportLoading({ reportId, loading: false });
        }
      }
    },
    async createReport({ reportPayload, mixpanelPayload = {} }) {
      const response = await DashboardAPI.createReport({
        dashboardId: this.currentDashboardId,
        data: reportPayload,
      });
      const reportId = response?.data?.id;
      const reportMeta = reportPayload?.meta ?? {};

      mixpanelTracker.dashboardCreateReport({
        reportId,
        reportType: reportPayload?.type,
        reportMeta,
        dashboard: this,
        messageTagFilterOptions: mixpanelPayload.messageTagFilterOptions,
        reportCategory: mixpanelPayload.reportCategory,
      });

      // Add report to layout
      const newLayout = V1LayoutAdapter.addReport(this.currentDashboard?.layout || [], {
        reportId,
        type: reportPayload.type,
      });
      await this.internalUpdateDashboard({ ...this.currentDashboard, layout: newLayout });

      return reportId;
    },
    async createReportsBatch({ reports, layoutPlacements, mixpanelPayload = {} }) {
      const response = await DashboardAPI.createReportsBatch({
        dashboardId: this.currentDashboardId,
        data: { reports, layout_placements: layoutPlacements },
      });
      const { reports: created = [], errors = [], dashboard } = response?.data ?? {};

      created.forEach((report) => {
        mixpanelTracker.dashboardCreateReport({
          reportId: report.id,
          reportType: report.type,
          reportMeta: report.meta ?? {},
          dashboard: this,
          messageTagFilterOptions: mixpanelPayload.messageTagFilterOptions,
          reportCategory: mixpanelPayload.reportCategory,
          // Active metric-selector tab at submit time ('single' | 'multi').
          reportFormTab: mixpanelPayload.reportFormTab,
        });
      });

      // Server returns the authoritative layout — apply locally without a follow-up PATCH.
      if (dashboard && this.currentDashboard?.id === dashboard.id) {
        this.currentDashboard = { ...this.currentDashboard, layout: dashboard.layout };
      }

      return { reports: created, errors };
    },
    refreshReportData() {
      // dump the cache of report data so that ALL reports will be fetched
      // fresh from the API the next time they are loaded
      this.clearReports();
      const layoutReportProps = (this.currentDashboard?.layout || []).map((item) => ({
        reportId: item.report_id,
        reportType: item.type,
      }));
      return Promise.all(
        layoutReportProps.map((report) =>
          this.getReportData({
            reportId: report.reportId,
            refresh: true,
          }),
        ),
      );
    },
    updateDateRanges({ reportDateRange, contextDateRange }) {
      const authStore = useAuthStore();
      const identity = authStore.identity;
      const dashboardReportsStore = useDashboardReportsStore();
      const updateReportDateRange =
        !!reportDateRange && !isEqual(reportDateRange, dashboardReportsStore.reportDateRange);
      const updateContextDateRange =
        !!contextDateRange && !isEqual(contextDateRange, dashboardReportsStore.contextDateRange);

      if (updateReportDateRange) {
        dashboardReportsStore.updateReportDate({
          newReportDateRange: reportDateRange,
          currentDashboard: this.currentDashboard,
          timeZone: identity?.time_zone_name,
        });
        useTrackingStore().track('Dashboard Sort Reporting Period', {
          dashboardId: this.currentDashboardId,
          dashboardName: this.currentDashboardName,
          dashboardStartDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[0],
          dashboardEndDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[1],
          dashboardContextStartDate:
            dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[0],
          dashboardContextEndDate:
            dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[1],
        });
      }

      if (updateContextDateRange) {
        dashboardReportsStore.updateContextDate({
          newContextDateRange: contextDateRange,
          currentDashboard: this.currentDashboard,
          timeZone: identity?.time_zone_name,
        });
        useTrackingStore().track('Dashboard Sort Comparison Period', {
          dashboardId: this.currentDashboardId,
          dashboardName: this.currentDashboardName,
          dashboardStartDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[0],
          dashboardEndDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[1],
          dashboardContextStartDate:
            dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[0],
          dashboardContextEndDate:
            dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[1],
        });
      }

      if (updateReportDateRange || updateContextDateRange) {
        void this.refreshReportData();
      }
    },
    async deleteReport(reportToDelete) {
      const reportId = reportToDelete?.id;

      try {
        await DashboardAPI.deleteReport({
          dashboardId: this.currentDashboardId,
          reportId,
        });

        mixpanelTracker.dashboardDeleteReport({
          reportId,
          reportType: reportToDelete?.type,
          reportMeta: reportToDelete?.meta ?? {},
          dashboard: this,
        });
      } catch (e) {
        if (e.response?.status !== 404) {
          throw e;
        }
      }

      // Remove report from layout
      const newLayout = V1LayoutAdapter.removeReport(this.currentDashboard?.layout || [], reportId);
      await this.internalUpdateDashboard({ ...this.currentDashboard, layout: newLayout });
      this.removeReport(reportToDelete);
    },
    async deleteRemovedReports(reportIds) {
      if (reportIds.length === 0) return;

      const promises = reportIds.map((reportId) =>
        DashboardAPI.deleteReport({
          dashboardId: this.currentDashboardId,
          reportId,
        }),
      );

      const results = await Promise.allSettled(promises);

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          logger.error(
            'Failed to delete report during customization cleanup',
            { reportId: reportIds[index] },
            result.reason,
          );
        }
      });
    },
    async getGenericReportData({ type, meta }) {
      const dashboardReportsStore = useDashboardReportsStore();
      this.previewReportData = null;
      const brandIds = Array.isArray(meta?.brand_ids) ? meta.brand_ids.join(',') : meta?.brand_ids;
      const metric = meta?.metric || meta?.metrics?.[0];
      const channel = isUGCMetric(metric) ? CHANNELS.INSTAGRAM_UGC.value : meta?.channel;
      if (
        ![REPORTS.SINGLE_METRIC.value, REPORTS.MULTI_BRAND_METRIC.value].includes(type) ||
        !brandIds ||
        !channel ||
        !metric
      )
        return;

      try {
        const response = await DashboardAPI.getGenericReportData({
          brandIds,
          channels: channel,
          metrics: metric,
          startDate: dashboardReportsStore.reportDateRangeInUserTimezone?.[0],
          endDate: dashboardReportsStore.reportDateRangeInUserTimezone?.[1],
          contextStartDate: dashboardReportsStore.contextDateRangeInUserTimezone?.[0],
          contextEndDate: dashboardReportsStore.contextDateRangeInUserTimezone?.[1],
        });
        this.previewReportData = response?.data;
      } catch (error) {
        if (!isCancelError(error)) throw error;
      }
    },
    async getReportsData({
      type,
      brandIds,
      competitorSourceAccountIds,
      channels,
      metrics,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
    }) {
      const dashboardReportsStore = useDashboardReportsStore();
      const request = {
        reportType: type,
        brandIds,
        competitorSourceAccountIds,
        channels,
        metrics,
        aggregateBy,
        startDate: startDate ?? dashboardReportsStore.reportDateRangeInUserTimezone?.[0],
        endDate: endDate ?? dashboardReportsStore.reportDateRangeInUserTimezone?.[1],
        contextStartDate:
          contextStartDate ?? dashboardReportsStore.contextDateRangeInUserTimezone?.[0],
        contextEndDate: contextEndDate ?? dashboardReportsStore.contextDateRangeInUserTimezone?.[1],
        sourceAdAccountIds,
        sourceCampaignIds,
      };

      const cachedReportsData = this.reportsDataCache.find((cacheValue) =>
        isEqual(cacheValue.request, request),
      );
      if (cachedReportsData) {
        return cachedReportsData?.payload;
      }

      try {
        const response = await DashboardAPI.getGenericReportData(request);
        const payload = response?.data;
        this.reportsDataCache.push({
          request,
          payload,
        });
        return payload;
      } catch (error) {
        if (!isCancelError(error)) throw error;
      }
      return {};
    },
    async updateReport({ id, type, meta, inlineEdit, mixpanelPayload = {} }) {
      const previousReportMeta = this.reports[id]?.meta;
      const firstMeta = meta;

      await DashboardAPI.updateReport({
        dashboardId: this.currentDashboardId,
        reportId: id,
        data: {
          meta: firstMeta,
          type,
        },
      });

      mixpanelTracker.dashboardEditReport({
        reportId: id,
        reportType: type,
        firstMeta,
        previousReportMeta,
        dashboard: this,
        inlineEdit,
        messageTagFilterOptions: mixpanelPayload.messageTagFilterOptions,
      });

      await this.getReportData({ reportId: id, refresh: true });
    },

    async duplicateReport({ type, meta, mixpanelPayload = {} }) {
      const response = await DashboardAPI.createReport({
        dashboardId: this.currentDashboardId,
        data: {
          type,
          meta,
        },
      });
      const reportId = response?.data?.id;

      mixpanelTracker.dashboardCreateReport({
        reportId,
        reportType: type,
        reportMeta: meta,
        dashboard: this,
        messageTagFilterOptions: mixpanelPayload.messageTagFilterOptions,
        duplicate: true,
        reportCategory: mixpanelPayload.reportCategory,
      });

      await this.getReportData({ reportId });

      // Add report to layout
      const newLayout = V1LayoutAdapter.addReport(this.currentDashboard?.layout || [], {
        reportId,
        type,
      });
      await this.internalUpdateDashboard({ ...this.currentDashboard, layout: newLayout });

      return reportId;
    },
    openReportStaticDates({ reportId }) {
      this.showStaticDatesDropdownReportId = reportId;
    },
    closeReportStaticStates() {
      this.showStaticDatesDropdownReportId = null;
    },
    async saveReportCustomName({ reportId, customName }) {
      if (!this.reports[reportId]) {
        return null;
      }

      const report = this.reports[reportId];
      const id = report?.id;
      const type = report?.type;
      const meta = cloneDeep(report?.meta) ?? {};

      if (!customName && meta.custom_name) delete meta.custom_name;
      else {
        meta.custom_name = customName;
      }
      const response = await this.updateReport({ id, type, meta });
      const payload = response?.data;
      return payload;
    },
    async saveReportStaticDates({
      reportId,
      reportDateRange,
      contextDateRange,
      dateRemovedLocation = null,
    }) {
      const dashboardReportsStore = useDashboardReportsStore();
      let mixpanelPayload = {};
      if (!this.reports[reportId]) {
        return null;
      }

      const report = this.reports[reportId];
      const id = report?.id;
      const type = report?.type;
      const meta = cloneDeep(report?.meta) ?? {};
      const isCustomMetric = meta?.metrics?.[0]?.startsWith('CM_');

      if (isCustomMetric) {
        if (meta?.channel) delete meta.channel;
        if (meta?.channels) delete meta.channels;
      }
      if (meta?.adjusted_dates) delete meta.adjusted_dates;
      if (reportDateRange?.length > 1) {
        if (!meta?.static_dates) {
          meta.static_dates = {};
        }
        meta.static_dates.start_date = reportDateRange[0];
        meta.static_dates.end_date = reportDateRange[1];
        mixpanelPayload.reportStartDate = meta.static_dates.start_date;
        mixpanelPayload.reportEndDate = meta.static_dates.end_date;
        meta.static_dates.rolling_time = false;
        if (contextDateRange?.length > 1) {
          meta.static_dates.context_start_date = contextDateRange[0];
          meta.static_dates.context_end_date = contextDateRange[1];
          mixpanelPayload.reportContextStartDate = meta.static_dates.context_start_date;
          mixpanelPayload.reportContextEndDate = meta.static_dates.context_end_date;
        }
      } else {
        meta.static_dates = undefined;
      }

      const response = await this.updateReport({ id, type, meta });
      const payload = response?.data;

      mixpanelPayload = {
        ...mixpanelPayload,
        customDateRemoved: meta.static_dates === undefined,
        customDateRemovedMethod: dateRemovedLocation,
        dashboardId: this.currentDashboard?.id,
        dashboardName: this.currentDashboard?.name,
        reportID: id,
        reportMetrics: meta?.metrics || [].concat(meta?.metric || []),
        reportType: type,
        dashboardStartDate: dashboardReportsStore.reportDateRange[0],
        dashboardEndDate: dashboardReportsStore.reportDateRange[1],
        dashboardContextStartDate: dashboardReportsStore.contextDateRange[0],
        dashboardContextEndDate: dashboardReportsStore.contextDateRange[1],
      };

      useTrackingStore().track('Dashboard Report Date Changed', mixpanelPayload);

      return payload;
    },
    async deleteReportStaticDates({ reportId, removedByDashboard = false }) {
      try {
        return await this.saveReportStaticDates({
          reportId,
          reportDateRange: null,
          contextDateRange: null,
          dateRemovedLocation: removedByDashboard ? 'DASHBOARD' : 'REPORT',
        });
      } catch (error) {
        logger.error(
          'Failed to delete report static dates',
          {
            reportId,
          },
          error,
        );
        throw error;
      }
    },

    async deleteDashboardStaticDates() {
      const ids = this.reportsWithStaticDates.map((report) => report.id);
      if (ids?.length > 0) {
        await Promise.all(
          ids.map((reportId) =>
            this.deleteReportStaticDates({ reportId, removedByDashboard: true }),
          ),
        );
      }
    },

    // Exports - delegated to dashboards-exports store for backwards compatibility
    launchScheduleAutoExportForm(autoExport = null) {
      useDashboardExportsStore().launchScheduleAutoExportForm(autoExport);
    },
    closeScheduleAutoExportForm() {
      useDashboardExportsStore().closeScheduleAutoExportForm();
    },
    launchAutoExportList() {
      useDashboardExportsStore().launchAutoExportList();
    },
    closeAutoExportList() {
      useDashboardExportsStore().closeAutoExportList();
    },
    setDownloadingSpreadsheetExport(downloadingSpreadsheetExport) {
      useDashboardExportsStore().setDownloadingSpreadsheetExport(downloadingSpreadsheetExport);
    },
    updateAllDashboardAutoExports(autoExports) {
      useDashboardExportsStore().updateAllDashboardAutoExports(autoExports);
    },
    updateCurrentDashboardAutoExports(autoExports) {
      useDashboardExportsStore().updateCurrentDashboardAutoExports(autoExports);
    },
    downloadingReportDataCsv(loading) {
      useDashboardExportsStore().setReportDataCsvLoading(loading);
    },
    async downloadDashboardPDF(options) {
      return useDashboardExportsStore().downloadDashboardPDF(options);
    },
    async downloadDashboardSpreadsheet(options) {
      return useDashboardExportsStore().downloadDashboardSpreadsheet(options);
    },
    async downloadReportCSV(options) {
      return useDashboardExportsStore().downloadReportCSV(options);
    },
    async getAllDashboardAutoExports() {
      return useDashboardExportsStore().getAllDashboardAutoExports();
    },
    async refreshDashboardAutoExports() {
      return useDashboardExportsStore().refreshDashboardAutoExports();
    },
    async createAutoExport(options) {
      return useDashboardExportsStore().createAutoExport(options);
    },
    async updateAutoExport(options) {
      return useDashboardExportsStore().updateAutoExport(options);
    },
    async unsubscribeAutoExport(options) {
      return useDashboardExportsStore().unsubscribeAutoExport(options);
    },
    async removeAutomatedExport(options) {
      return useDashboardExportsStore().removeAutomatedExport(options);
    },
    async downloadReportDataCSV(options) {
      return useDashboardExportsStore().downloadReportDataCSV(options);
    },
    // Sharing - delegated to dashboards-sharing store for backwards compatibility
    launchShareForm() {
      useDashboardSharingStore().launchShareForm();
    },
    closeShareForm() {
      useDashboardSharingStore().closeShareForm();
    },
    setIsDashboardUserUpdating(newValue) {
      useDashboardSharingStore().setIsDashboardUserUpdating(newValue);
    },
    setDashboardShareableUsers(dashboardSharableUsers) {
      useDashboardSharingStore().setDashboardShareableUsers(dashboardSharableUsers);
    },
    updateDashboardShareUserSearchTerm(dashboardShareUserSearchTerm) {
      useDashboardSharingStore().updateDashboardShareUserSearchTerm(dashboardShareUserSearchTerm);
    },
    updateCurrentDashboardUsers(users) {
      useDashboardSharingStore().updateCurrentDashboardUsers(users);
    },
    async removeDashboardUser(options) {
      return useDashboardSharingStore().removeDashboardUser(options);
    },
    async leaveDashboard() {
      return useDashboardSharingStore().leaveDashboard();
    },
    async getAllAccessibleBrandsUsers(options) {
      return useDashboardSharingStore().getAllAccessibleBrandsUsers(options);
    },
    async getDashboardSharableUsers(options) {
      return useDashboardSharingStore().getDashboardSharableUsers(options);
    },
    async refreshDashboardUsers() {
      return useDashboardSharingStore().refreshDashboardUsers();
    },
    async shareDashboard(options) {
      return useDashboardSharingStore().shareDashboard(options);
    },
    async transferOwnership(options) {
      return useDashboardSharingStore().transferOwnership(options);
    },
    // Industries
    async fetchIndustries() {
      const response = await DashboardAPI.getIndustries();
      this.industries = response?.data;
    },
  },
});
